import React, { useEffect, useState } from 'react';
import { adminBlogService } from '../services';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { notifyError, notifyInfo, notifySuccess } from '../utils/toast';

const Blogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        is_published: false,
        featured_image: null,
    });

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            const response = await adminBlogService.getAll();
            setBlogs(response.data.data || []);
        } catch (err) {
            console.error('Error fetching blogs:', err);
            notifyError('Unable to load blogs.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files?.[0] || null : value,
        }));
    };

    const resetForm = () => {
        setFormData({
            title: '',
            content: '',
            is_published: false,
            featured_image: null,
        });
        setError('');
        setEditingId(null);
        setShowForm(false);
    };

    const handleEdit = (blog) => {
        setFormData({
            title: blog.title,
            content: blog.content,
            is_published: !!blog.is_published,
            featured_image: null,
        });
        setEditingId(blog.id);
        setShowForm(true);
        notifyInfo('Blog loaded for editing.');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            if (editingId) {
                await adminBlogService.update(editingId, formData);
                notifySuccess('Blog updated successfully.');
            } else {
                await adminBlogService.create(formData);
                notifySuccess('Blog created successfully.');
            }
            await fetchBlogs();
            resetForm();
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to save blog post.');
            notifyError(err.response?.data?.message || 'Unable to save blog post.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this blog post?')) return;
        try {
            await adminBlogService.delete(id);
            await fetchBlogs();
            notifySuccess('Blog deleted successfully.');
        } catch (err) {
            console.error('Error deleting blog:', err);
            notifyError('Unable to delete blog.');
        }
    };

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <h1 className="text-4xl font-bold text-gray-800">Blogs</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-white transition hover:bg-emerald-700"
                >
                    <FaPlus /> Add Blog
                </button>
            </div>

            {showForm && (
                <div className="mb-8 rounded-lg bg-white p-8 shadow-lg">
                    <h2 className="mb-6 text-2xl font-bold text-gray-800">{editingId ? 'Edit Blog' : 'New Blog'}</h2>
                    {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">{error}</div>}
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Title</label>
                            <input name="title" value={formData.title} onChange={handleChange} required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Content</label>
                            <textarea name="content" value={formData.content} onChange={handleChange} rows="6" required className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <div>
                            <label className="mb-2 block font-semibold text-gray-700">Featured Image</label>
                            <input type="file" name="featured_image" accept="image/*" onChange={handleChange} className="w-full rounded-lg border border-gray-300 px-4 py-2" />
                        </div>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" name="is_published" checked={formData.is_published} onChange={handleChange} />
                            <span className="font-semibold text-gray-700">Publish now</span>
                        </label>
                        <div className="flex gap-4">
                            <button type="submit" className="rounded-lg bg-emerald-600 px-6 py-2 text-white hover:bg-emerald-700">Save</button>
                            <button type="button" onClick={resetForm} className="rounded-lg bg-gray-400 px-6 py-2 text-white hover:bg-gray-500">Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="py-12 text-center">
                    <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg bg-white shadow-lg">
                    <table className="w-full">
                        <thead className="bg-emerald-600 text-white">
                            <tr>
                                <th className="px-6 py-3 text-left">Title</th>
                                <th className="px-6 py-3 text-left">Published</th>
                                <th className="px-6 py-3 text-left">Date</th>
                                <th className="px-6 py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {blogs.map((blog) => (
                                <tr key={blog.id} className="border-b hover:bg-gray-50">
                                    <td className="px-6 py-4">{blog.title}</td>
                                    <td className="px-6 py-4">{blog.is_published ? 'Yes' : 'No'}</td>
                                    <td className="px-6 py-4">{blog.published_at ? new Date(blog.published_at).toLocaleDateString() : '-'}</td>
                                    <td className="flex gap-3 px-6 py-4">
                                        <button onClick={() => handleEdit(blog)} className="text-emerald-600 hover:text-emerald-800"><FaEdit /></button>
                                        <button onClick={() => handleDelete(blog.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Blogs;
